import React, { useRef, useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { images } from "../../constants";
import "./InteractiveBender.scss";

const QUIPS = [
  "I'm 40% Python!",
  "Bite my shiny metal codebase.",
  "Hire the human. I taught him everything he knows.",
  "01001000 01101001. That's 'Hi', meatbag.",
  "Kill all bugs!",
  "Neat portfolio. I'd steal it, but it's open source.",
  "Try Ctrl+K. I dare you.",
];

// Eye geometry as percentages of the rendered image box.
// Each eye gets a cream "window" that covers the painted pupil, with our own
// pupil translating inside it toward the cursor.
const EYES = [
  { id: "left", left: 25.0, top: 21.8, width: 14.5, height: 5.6 },
  { id: "right", left: 41.0, top: 22.2, width: 12.5, height: 5.4 },
];

// Max pupil deflection as a fraction of the eye window size
const MAX_DEFLECT_X = 0.26;
const MAX_DEFLECT_Y = 0.2;

const InteractiveBender = () => {
  const wrapperRef = useRef(null);
  const pupilRefs = useRef([]);
  const quipTimerRef = useRef(null);
  const [quip, setQuip] = useState(null);
  const lastQuipRef = useRef(-1);

  // Cursor-tracking pupils — direct DOM updates via refs, rAF-throttled,
  // no React re-render per mousemove
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let rafId = null;
    let cursor = null;

    const update = () => {
      rafId = null;
      const wrapper = wrapperRef.current;
      if (!wrapper || !cursor) return;
      const rect = wrapper.getBoundingClientRect();
      if (rect.width === 0) return;

      EYES.forEach((eye, i) => {
        const pupil = pupilRefs.current[i];
        if (!pupil) return;
        const eyeW = (eye.width / 100) * rect.width;
        const eyeH = (eye.height / 100) * rect.height;
        const cx = rect.left + ((eye.left + eye.width / 2) / 100) * rect.width;
        const cy = rect.top + ((eye.top + eye.height / 2) / 100) * rect.height;
        const dx = cursor.x - cx;
        const dy = cursor.y - cy;
        const dist = Math.hypot(dx, dy);
        // Full deflection once the cursor is ~250px away
        const f = Math.min(1, dist / 250);
        const angle = Math.atan2(dy, dx);
        const tx = Math.cos(angle) * eyeW * MAX_DEFLECT_X * f;
        const ty = Math.sin(angle) * eyeH * MAX_DEFLECT_Y * f * 2.2;
        pupil.style.transform = `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px))`;
      });
    };

    const onMouseMove = (e) => {
      cursor = { x: e.clientX, y: e.clientY };
      if (rafId === null) rafId = requestAnimationFrame(update);
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  const speak = useCallback(() => {
    let next;
    do {
      next = Math.floor(Math.random() * QUIPS.length);
    } while (next === lastQuipRef.current && QUIPS.length > 1);
    lastQuipRef.current = next;
    setQuip(QUIPS[next]);
    clearTimeout(quipTimerRef.current);
    quipTimerRef.current = setTimeout(() => setQuip(null), 5000);
  }, []);

  useEffect(() => () => clearTimeout(quipTimerRef.current), []);

  return (
    <div
      className="interactive-bender"
      ref={wrapperRef}
      onClick={speak}
      onMouseDown={(e) => e.preventDefault()} // don't grab focus on mouse click; Tab still focuses
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && speak()}
      aria-label="Bender — click for a quip"
    >
      <img src={images.profile} alt="Bender the robot" draggable={false} />

      {EYES.map((eye, i) => (
        <div
          key={eye.id}
          className="bender-eye"
          style={{
            left: `${eye.left}%`,
            top: `${eye.top}%`,
            width: `${eye.width}%`,
            height: `${eye.height}%`,
          }}
        >
          <div
            className="bender-pupil"
            ref={(el) => (pupilRefs.current[i] = el)}
          />
        </div>
      ))}

      <AnimatePresence>
        {quip && (
          <motion.div
            className="bender-quip"
            initial={{ opacity: 0, scale: 0.6, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: -6 }}
            transition={{ type: "spring", stiffness: 400, damping: 22 }}
          >
            {quip}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InteractiveBender;
